import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import Button from "components/button";
import ConfirmationModal from "components/shared/confirmation-modal";

import useCreateDriverForm from "hooks/driver/useCreateDriverForm";

import DriverAccountAccessAccordion from "./components/driver-account-access-accordion";
import DriverEmergencyAccordion from "./components/driver-emergency-accordion";
import DriverFinancialDocumentsAccordion from "./components/driver-financial-documents-accordion";
import DriverProfileAccordion from "./components/driver-profile-accordion";
import { Loader } from "components/spinner/loader";

const AddDriverPage = () => {
  const {
    t,
    form,
    openSections,
    toggleSection,
    vendorOptions,
    isLoadingVendorOptions,
    isCreatingDriver,
    isUploadingDriverPicture,
    onSearchVendors,
    driverPictureName,
    driverPictureUrl,
    driverPictureError,
    validatePicture,
    documentFiles,
    documentErrors,
    validateDocuments,
    expiryDates,
    handleExpiryDateChange,
    isDirty,
    handleNextFromProfile,
    handleNextFromEmergency,
    handleNextFromFinancial,
    handleDriverPictureChange,
    handleDocumentFileChange,
    handleSaveDraft,
    handleSave,
    showCancelModal,
    setShowCancelModal,
    navigate,
    isLoading,
    handleDiscard,
  } = useCreateDriverForm();

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    const handleGlobalBack = (e: Event) => {
      if (isDirty) {
        e.preventDefault();
        setShowUnsavedModal(true);
      }
    };

    window.addEventListener("global-back-button-click", handleGlobalBack);

    return () =>
      window.removeEventListener("global-back-button-click", handleGlobalBack);
  }, [isDirty]);

  const handleLocalDiscard = useCallback(() => {
    setShowUnsavedModal(false);
    handleDiscard();
  }, [handleDiscard]);

  const handleConfirmSaveDraft = useCallback(async () => {
    setShowUnsavedModal(false);
    await handleSaveDraft();
  }, [handleSaveDraft]);

  const handleConfirmSave = useCallback(async () => {
    setShowSaveModal(false);
    await handleSave();
  }, [handleSave]);

  const handleSaveClick = useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      toast.error(
        t("toast-complete-required-fields") ||
          "Please complete required fields"
      );

      return;
    }

    const isPicValid = validatePicture();
    if (!isPicValid) {
      return;
    }

    const isDocsValid = validateDocuments();
    if (!isDocsValid) {
      return;
    }

    setShowSaveModal(true);
  }, [form, t, validatePicture, validateDocuments]);

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-200px)] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-112px)] -mb-3 -mx-3">
      <div className="flex-1 flex flex-col gap-4 p-3 pb-20">
        <DriverProfileAccordion
          isOpen={openSections.driverProfile}
          onToggle={() => toggleSection("driverProfile")}
          onNext={handleNextFromProfile}
          form={form}
          t={t}
          vendorOptions={vendorOptions}
          isLoadingVendorOptions={isLoadingVendorOptions}
          onSearchVendors={onSearchVendors}
          driverPictureName={driverPictureName}
          driverPictureUrl={driverPictureUrl}
          driverPictureError={driverPictureError}
          isUploadingDriverPicture={isUploadingDriverPicture}
          onDriverPictureChange={handleDriverPictureChange}
        />

        <DriverEmergencyAccordion
          isOpen={openSections.emergencyContacts}
          onToggle={() => toggleSection("emergencyContacts")}
          onNext={handleNextFromEmergency}
          form={form}
          t={t}
        />

        <DriverFinancialDocumentsAccordion
          isOpen={openSections.financialDocuments}
          onToggle={() => toggleSection("financialDocuments")}
          onNext={handleNextFromFinancial}
          form={form}
          t={t}
          documentFiles={documentFiles}
          documentErrors={documentErrors}
          onDocumentFileChange={handleDocumentFileChange}
          expiryDates={expiryDates}
          onExpiryDateChange={handleExpiryDateChange}
        />

        <DriverAccountAccessAccordion
          isOpen={openSections.accountAccess}
          onToggle={() => toggleSection("accountAccess")}
          form={form}
          t={t}
        />
      </div>

      <div className="sticky bottom-0 bg-white border-t border-border px-6 py-3 flex justify-end gap-2 z-20 mt-auto">
        <Button
          type="button"
          variant="outline"
          color="secondary"
          onClick={handleSaveDraft}
          disabled={isCreatingDriver || isUploadingDriverPicture}
          data-testid="btn-save-draft"
        >
          {t("btn-save-draft")}
        </Button>

        <Button
          type="button"
          variant="fill"
          color="primary"
          onClick={handleSaveClick}
          disabled={isCreatingDriver || isUploadingDriverPicture}
          data-testid="btn-save-driver"
        >
          {t("btn-save")}
        </Button>
      </div>

      <ConfirmationModal
        open={showUnsavedModal}
        title={t("modal-unsaved-title") || "You have unsaved changes"}
        message={
          t("modal-unsaved-message") ||
          "This action cannot be undone. Save as draft or discard before leaving."
        }
        cancelLabel={t("btn-discard-changes") || "Discard Changes"}
        confirmLabel={t("btn-save-as-draft") || "Save as Draft"}
        onCancel={handleLocalDiscard}
        onConfirm={handleConfirmSaveDraft}
        icon="info"
      />

      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title") || "Save this data?"}
        message={
          t("modal-save-message") ||
          "Are you sure you want to save this information?"
        }
        cancelLabel={t("btn-cancel") || "Cancel"}
        confirmLabel={t("btn-save") || "Save"}
        onCancel={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        icon="info"
        loading={isCreatingDriver}
      />

      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-save-draft-title") || "Save as Draft?"}
        message={
          t("modal-save-draft-message") ||
          "You have unsaved changes. Do you want to save this driver as a draft?"
        }
        confirmLabel={t("btn-save-draft") || "Save as Draft"}
        cancelLabel={t("modal-confirm-stay") || "Keep Editing"}
        onConfirm={() => {
          setShowCancelModal(false);
          handleSaveDraft();
        }}
        onCancel={() => setShowCancelModal(false)}
      >
        <div className="mt-2 mb-2 w-full px-2">
          <button
            type="button"
            className="w-full text-center text-sm font-medium text-error hover:underline"
            onClick={handleDiscard}
          >
            {t("modal-confirm-discard") || "Discard and leave"}
          </button>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default AddDriverPage;
